import { config } from "../../config/config";
import { authHeaders, handleError } from "./_helpers";
import {
  appBannerReviewItemSchema,
  appBannerReviewsResponseSchema,
  type AppBannerReviewItem,
  type AppBannerReviewsResponse,
} from "../../utils/zod/adminAppBanners";
import type { AppBannerReviewFilter } from "../../utils/zod/adminAppBanners";

const BASE_URL = `${config.API_URL}/v1/admin/app-banners`;

const FORMAT_ERROR = "La cola de banners llegó con un formato inesperado";

export const getAppBannerReviewsRequest = async (
  params: { status: AppBannerReviewFilter; page: number; limit: number },
  token: string,
): Promise<AppBannerReviewsResponse> => {
  const query = new URLSearchParams({
    status: params.status,
    page: String(params.page),
    limit: String(params.limit),
  });

  const response = await fetch(`${BASE_URL}?${query}`, {
    method: "GET",
    headers: authHeaders(token),
  });

  if (!response.ok)
    await handleError(response, "Error al obtener los banners a revisar");

  const parsed = appBannerReviewsResponseSchema.safeParse(
    await response.json(),
  );
  if (!parsed.success) throw new Error(FORMAT_ERROR);

  return parsed.data;
};

const resolveRequest = async (
  bannerPublicId: string,
  action: "approve" | "reject",
  token: string,
  body?: { reason: string },
): Promise<AppBannerReviewItem> => {
  const response = await fetch(`${BASE_URL}/${bannerPublicId}/${action}`, {
    method: "POST",
    headers: authHeaders(token),
    ...(body && { body: JSON.stringify(body) }),
  });

  if (!response.ok) await handleError(response, "Error al revisar el banner");

  const parsed = appBannerReviewItemSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error(FORMAT_ERROR);

  return parsed.data;
};

export const approveAppBannerRequest = (
  bannerPublicId: string,
  token: string,
): Promise<AppBannerReviewItem> =>
  resolveRequest(bannerPublicId, "approve", token);

export const rejectAppBannerRequest = (
  bannerPublicId: string,
  reason: string,
  token: string,
): Promise<AppBannerReviewItem> =>
  resolveRequest(bannerPublicId, "reject", token, { reason });
